import { useMemo } from 'react';
import { Extensions } from '@tiptap/core';
import Document from '@tiptap/extension-document';

import useClientCommonTiptapEditorExtensions from '../../../components/tiptapEditor/useClientCommonTiptapEditorExtensions';
import type { SaveTiptapEditorContentFunction } from '../../../../common/tiptap/extensions/externalStoreSync/externalStoreSynchroniserTypes';
import FormulaCellHighlightTiptapExtension from '../plugins/FormulaCellReferenceHotPlugin/FormulaCellHighlightTiptapExtension';
import TableCellCommitEditTiptapExtension from './TableCellCommitEditTiptapExtension';

type UseTableCellEditorTiptapExtensionsArgs = {
    isEditable: boolean;
    saveContent?: SaveTiptapEditorContentFunction;
    onCommitEdit: (event: KeyboardEvent) => void;
};

export const useTableCellEditorTiptapExtensions = ({
    isEditable,
    saveContent,
    onCommitEdit,
}: UseTableCellEditorTiptapExtensionsArgs): Extensions => {
    const commonExtensions = useClientCommonTiptapEditorExtensions({
        saveContent,
        // The cell editor dispatches its edits to Handsontable, not redux element update,
        // so a low debounce time is safe. Things like the formula autocomplete popup will wait
        // for the debounce before displaying, so lower = better.
        getDebounceTime: () => 1,
        // The editor can be non-editable in some situations, eg when a formatting popup is open
        isEditable,
    });

    return useMemo(
        () =>
            [
                Document,
                FormulaCellHighlightTiptapExtension,
                TableCellCommitEditTiptapExtension.configure({ onCommitEdit }),
                ...commonExtensions,
            ] as Extensions,
        [commonExtensions, onCommitEdit],
    );
};

export const useTableCellRendererTiptapExtensions = (): Extensions => {
    const commonExtensions = useClientCommonTiptapEditorExtensions({
        isEditable: false,
    });

    return useMemo(
        () => [Document, FormulaCellHighlightTiptapExtension, ...commonExtensions] as Extensions,
        [commonExtensions],
    );
};
